/** Einstellungen › Abo (E13.2): Testphase, Abo-Status, Zahlung über Stripe (Checkout und Kundenportal). */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { Button, Notice, Seitenzustand, StatusBadge } from "../ui";
import { Card, Dl, PageHeader } from "../ui/Layout";
import { api, rufe } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag } from "@shared/zeit";
import { aboStatusText, type AboStatus } from "@shared/schemas/anbieter";

export interface AboDaten {
  aboStatus: AboStatus;
  testphaseBis: string | null;
  aboBis: string | null;
  testTageRest: number | null;
  gesperrt: boolean;
  grund: string | null;
  stripe: boolean;
  stripeEingerichtet: boolean;
  kunde: boolean;
  preis: { betragCent: number | null; intervall: string; waehrung: string; stufen: { bisMenge: number | null; betragCent: number }[] } | null;
  regale: number;
  kannVerwalten: boolean;
  anbieterEmail: string | null;
}

export function useAbo() {
  return useQuery({ queryKey: ["laden", "abo"], queryFn: () => rufe<AboDaten>(api.api.laden.abo.$get()) });
}

export function Abo() {
  const abo = useAbo();
  const [params] = useSearchParams();
  const checkout = useMutation({ mutationFn: () => rufe<{ url: string }>(api.api.laden.abo.checkout.$post()), onSuccess: (r) => window.location.assign(r.url) });
  const portal = useMutation({ mutationFn: () => rufe<{ url: string }>(api.api.laden.abo.portal.$post()), onSuccess: (r) => window.location.assign(r.url) });
  const rueck = params.get("checkout");
  return (
    <>
      <PageHeader kicker="Einstellungen" title="Abo" sub="Testphase, Abo und Zahlung" />
      {rueck === "ok" ? <Notice tone="success" title="Danke, das Abo läuft">Die Bestätigung von Stripe kann eine Minute dauern. Danach steht hier „Abo aktiv“.</Notice> : null}
      {rueck === "abbruch" ? <Notice tone="info">Der Abschluss wurde abgebrochen. Du kannst ihn jederzeit erneut starten.</Notice> : null}
      <Seitenzustand query={abo}>
        {(d) => (
          <div className="sc-col" style={{ gap: "var(--space-4)" }}>
            {d.gesperrt ? (
              <Notice tone="attention" title="Neue Buchungen sind gesperrt">
                {d.grund} Lesen, Kassieren und Abrechnen bleiben möglich.
              </Notice>
            ) : null}
            <Card title="Stand">
              <Dl
                rows={[
                  ["Status", <StatusBadge status={d.aboStatus === "aktiv" || d.aboStatus === "frei" ? "paid" : d.aboStatus === "test" ? "wait" : "error"}>{aboStatusText(d.aboStatus)}</StatusBadge>],
                  ...(d.aboStatus === "test" && d.testphaseBis ? ([["Testphase bis", `${formatTag(d.testphaseBis)} (${d.testTageRest === 0 ? "heute" : `noch ${d.testTageRest} Tage`})`]] as [string, string][]) : []),
                  ...(d.aboStatus === "gekuendigt" && d.aboBis ? ([["Läuft bis", formatTag(d.aboBis)]] as [string, string][]) : []),
                  ["Aktive Regale", String(d.regale)],
                  ...(d.preis ? ([["Preis", preisText(d.preis)]] as [string, string][]) : []),
                ]}
              />
            </Card>
            {!d.stripeEingerichtet ? (
              <Notice tone="info" title="Abrechnung über den Anbieter">
                Die Zahlung per Karte oder Lastschrift ist noch nicht freigeschaltet. {d.anbieterEmail ? `Schreib an ${d.anbieterEmail}, dann wird das Abo von Hand eingerichtet.` : "Der Anbieter richtet das Abo auf Anfrage ein."}
              </Notice>
            ) : d.aboStatus === "frei" ? (
              <Notice tone="success" title="Kostenlos gestellt">Für diesen Laden fällt keine Gebühr an.</Notice>
            ) : (
              <Card title="Zahlung">
                <p className="sc-muted">Abgerechnet wird je aktivem Regal und Monat, die Regalzahl wird täglich an Stripe gemeldet. Kündigen ist jederzeit zum Monatsende möglich.</p>
                <div className="sc-toolbar">
                  {d.aboStatus === "test" || d.aboStatus === "gekuendigt" ? (
                    <Button variant="primary" icon="credit-card" onClick={() => checkout.mutate()} loading={checkout.isPending} disabled={!d.kannVerwalten}>
                      Abo starten
                    </Button>
                  ) : null}
                  {d.kunde ? (
                    <Button variant="secondary" icon="wallet" onClick={() => portal.mutate()} loading={portal.isPending} disabled={!d.kannVerwalten}>
                      Zahlung verwalten
                    </Button>
                  ) : null}
                </div>
                {!d.kannVerwalten ? <p className="sc-muted sc-small">Nur Personen mit dem Recht „Einstellungen“ können das Abo ändern.</p> : null}
                {checkout.isError ? <Notice tone="error">{checkout.error.message}</Notice> : null}
                {portal.isError ? <Notice tone="error">{portal.error.message}</Notice> : null}
              </Card>
            )}
          </div>
        )}
      </Seitenzustand>
    </>
  );
}

function preisText(p: NonNullable<AboDaten["preis"]>): string {
  const je = p.intervall === "year" ? "Jahr" : "Monat";
  if (p.stufen.length) return p.stufen.map((s) => `${s.bisMenge ? `bis ${s.bisMenge} Regale` : "darüber"}: ${formatEuro(s.betragCent)} je Regal und ${je}`).join(", ");
  return p.betragCent !== null ? `${formatEuro(p.betragCent)} je Regal und ${je}` : "siehe Stripe";
}
